import type { SpecType } from '@local/lib';
import type { StateCreator } from 'zustand';

import type { QualityFormulaStore } from '../qualityFormulaStore';

export interface WorkbenchSlice {
  /** Enables a spec (same effect as selecting it in the old quality-type filter) and focuses the
   * editor on it — used by the navigator's "+ Add Spec" menu and its compact "+" button. */
  addSpec: (specType: SpecType) => void;
  /** Whether the Playground replaces the focused spec editor; test values and results
   * remain in their own slices when switching views. Mutually exclusive with `isTemplateManagerOpen`. */
  isPlaygroundOpen: boolean;
  /** Whether the inline Template Manager replaces the focused spec editor/Playground — opening this
   * never discards any spec's own configuration, same as the Playground. Mutually exclusive with
   * `isPlaygroundOpen`. */
  isTemplateManagerOpen: boolean;
  /** Disables a spec (same effect as removing it from the old quality-type filter) — if it was the
   * focused editor, focus falls to another still-enabled spec, or to nothing if none remain. */
  removeSpec: (specType: SpecType) => void;
  /** Which single spec's editor is focused in the main panel — switching this never discards any
   * spec's own configuration, since every spec's fields live in `specStates` regardless of focus. */
  selectedSpecType: null | SpecType;
  selectSpec: (specType: SpecType) => void;
  setPlaygroundOpen: (open: boolean) => void;
  setTemplateManagerOpen: (open: boolean) => void;
}

export const createWorkbenchSlice: StateCreator<QualityFormulaStore, [], [], WorkbenchSlice> = (set, get) => ({
  addSpec: specType => {
    const { handleVisibleSpecTypesChange, visibleSpecTypes } = get();
    if (!visibleSpecTypes.includes(specType)) {
      handleVisibleSpecTypesChange([...visibleSpecTypes, specType]);
    }
    set({ selectedSpecType: specType });
  },

  isPlaygroundOpen: false,

  isTemplateManagerOpen: false,

  removeSpec: specType => {
    const { handleVisibleSpecTypesChange, selectedSpecType, visibleSpecTypes } = get();
    const nextVisibleTypes = visibleSpecTypes.filter(key => key !== specType);
    handleVisibleSpecTypesChange(nextVisibleTypes);
    set({
      ...(selectedSpecType === specType ? { selectedSpecType: nextVisibleTypes[0] ?? null } : {}),
      ...(nextVisibleTypes.length === 0 ? { isPlaygroundOpen: false } : {}),
    });
  },

  selectedSpecType: null,

  selectSpec: specType => {
    set({ selectedSpecType: specType });
  },

  setPlaygroundOpen: open => {
    set({ isPlaygroundOpen: open, ...(open ? { isTemplateManagerOpen: false } : {}) });
  },

  setTemplateManagerOpen: open => {
    set({ isTemplateManagerOpen: open, ...(open ? { isPlaygroundOpen: false } : {}) });
  },
});
